"use client";

import { useEffect, useMemo } from "react";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  useWalletBalance,
  useWalletAssets,
  useQueryUserBalance,
  useQueryUserProfile,
} from "../hooks/query/queries";
import { useAuth } from "../context/AuthContext";
import { useCurrencySelection } from "../context/CurrencySelectionContext";
import CurrencyFormatter from "../services/currencyFormatterService";
import { TradingQuickActions } from "./trading-quick-actions";
import { setShowBalances } from "../services/settingsService";
import { useToast } from "../hooks/use-toast";

const DigitalWalletGradient = () => {
  const { data: walletData } = useWalletBalance();
  const { data: walletAssetsData, isLoading: isLoadingAssets } =
    useWalletAssets();
  const assetsForUse = useMemo(
    () => walletAssetsData?.assets || [],
    [walletAssetsData?.assets]
  );
  const { user, settings, userProfile, updateSettings } = useAuth();
  const { toast } = useToast();
  const { selectedAsset, setSelectedAsset } = useCurrencySelection();
  const selectedAssetNames = ["USDT", "South African Rand"];

  useEffect(() => {
    if (assetsForUse.length > 0 && !selectedAsset) {
      const zarAsset = assetsForUse.find(
        (asset) =>
          asset.assetName === "South African Rand" || asset.asset === "ZAR"
      );
      if (zarAsset) {
        setSelectedAsset(zarAsset);
      } else if (assetsForUse.length > 0) {
        setSelectedAsset(assetsForUse[0]);
      }
    }
  }, [assetsForUse, selectedAsset, setSelectedAsset]);

  const handleSelect = (asset) => {
    setSelectedAsset(asset);
  };

  const handleToggleBalanceVisibility = async () => {
    if (!userProfile?._id) return;

    try {
      const apiCall = await setShowBalances(userProfile._id);

      if (apiCall?.status === 200 && apiCall?.data?.status === true) {
        const newSettings = apiCall.data.data;
        updateSettings(newSettings);
      } else {
        toast({
          description: "Error updating balance visibility",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        description: "Error updating balance visibility",
        variant: "destructive",
      });
    }
  };

  const formattedPortfolioValue = useMemo(() => {
    if (!walletAssetsData || !selectedAsset) {
      return walletData?.portfolioEstimate || "R 0.00";
    }

    if (settings?.digitalWallet?.showBalances === false) {
      return "****";
    }

    const formatter = new CurrencyFormatter({
      locale: "en-US",
      decimalPlaces: 2,
      cryptocurrencyDecimalPlaces: 2,
      stablecoins: ["USDT", "USDC", "DAI"],
    });

    const selectedAssetCode = selectedAsset.asset?.toUpperCase();
    let estimate = 0;

    if (selectedAssetCode === "ZAR") {
      estimate = walletAssetsData.zarEstimate || 0;
      return formatter.format(estimate, "fiat", "ZAR");
    } else if (selectedAssetCode === "USDT") {
      estimate = walletAssetsData.usdtEstimate || 0;
      return formatter.format(estimate, "crypto", "USDT", { showSymbol: true });
    }

    estimate = walletAssetsData.zarEstimate || 0;
    return formatter.format(estimate, "fiat", "ZAR");
  }, [walletAssetsData, selectedAsset, walletData, settings]);

  const authId = user?.sub || "";
  const { balance, isLoading: loading } = useQueryUserBalance(authId);
  const isFirstTimeUser = balance?.data?.data?.portfolioEstimateRaw > 0;
  useQueryUserProfile();

  return (
    <div
      className={`md:min-h-[441px] min-h-[371px] md:px-[27px] md:py-[34px] px-[20px] py-[30px] rounded-lg w-full flex flex-col ${isFirstTimeUser ? "old-user-bg-trading-dashboard-sm" : "bg-trading-dashboard-sm"}`}
    >
      <div className="flex items-start justify-between flex-col text-white flex-1">
        <div
          className={`md:text-[20px] text-[24px] font-semibold ${isFirstTimeUser ? "text-white hidden md:block" : "text-[#33125F]"}`}
        >
          Wallet Balance
        </div>
        {isFirstTimeUser && (
          <div
            className={`md:text-[20px] text-[24px] font-semibold text-white md:hidden block`}
          >
            Portfolio Value
          </div>
        )}
        <div className="flex flex-col w-full">
          {/* {isFirstTimeUser && (
            <div className="text-[#EBE9EF] text-[16px] font-normal flex items-center gap-2">
              <img src={FlowIcon} alt="flow" className="w-4 h-4" />
              (+2%) | +R97,32
            </div>
          )} */}
          <div className="flex items-center justify-between space-x-2 w-full">
            {loading ? (
              <Skeleton className="w-64 h-12 rounded-full bg-accent/30" />
            ) : (
              <div
                className={`text-[#EBE9EF] ${isFirstTimeUser ? "md:text-[60px] text-[40px]" : "text-[40px]"} font-semibold break-words whitespace-normal min-w-0 flex-1 overflow-hidden`}
              >
                {formattedPortfolioValue}
              </div>
            )}
            <div className="flex items-center gap-2">
              {isFirstTimeUser && (
                <button
                  onClick={handleToggleBalanceVisibility}
                  className="w-[48px] h-[48px] flex items-center justify-center rounded-full border border-secondary/50 hover:bg-background/30 hover:text-white transition-colors"
                  aria-label={
                    settings?.digitalWallet?.showBalances === false
                      ? "Show balance"
                      : "Hide balance"
                  }
                >
                  {settings?.digitalWallet?.showBalances === false ? (
                    <EyeOff className="w-[22px]" />
                  ) : (
                    <Eye className="w-[22px]" />
                  )}
                </button>
              )}
              <div className={`${isFirstTimeUser ? "" : "lg:hidden"}`}>
                {isLoadingAssets ? (
                  <Skeleton className="w-20 h-10 rounded-full bg-accent/30" />
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-white bg-transparent border-secondary/50 rounded-full hover:bg-background/30 hover:text-white h-[48px]"
                      >
                        {selectedAsset?.asset?.toUpperCase() || "ZAR"}{" "}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      sideOffset={8}
                      className="rounded-lg max-h-80 overflow-y-auto"
                    >
                      {assetsForUse
                        ?.filter((asset) =>
                          selectedAssetNames.includes(asset.assetName)
                        )
                        .map((asset) => (
                          <DropdownMenuItem asChild key={asset.id}>
                            <button
                              onClick={() => handleSelect(asset)}
                              className="w-full text-left flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent hover:border-rose-200"
                            >
                              <img
                                src={asset.image}
                                alt={asset.assetName}
                                className="w-6 h-6"
                              />
                              <div className="flex flex-col text-left">
                                <span>{asset.assetName}</span>
                                <span className="text-xs text-gray-500">
                                  {asset.asset?.toUpperCase()}
                                </span>
                              </div>
                            </button>
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
          <div className="lg:hidden">
            <TradingQuickActions />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalWalletGradient;
